import React from "react";
import { Heart, Users, Award, Shield, CheckCircle2 } from "lucide-react";
import { LeadConsultationForm } from "@/components/LeadConsultationForm";

export const AboutPage: React.FC = () => {
  return (
    <div className="pt-24 pb-20 bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <span className="text-xs font-extrabold uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 rounded-md">
            Our Culture & Heritage
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            About Feingold Property Management
          </h1>
          <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
            Feingold Property Management, LLC (Feingold) is one of the most
            engaging, customer-focused, innovative, and technologically savvy
            management companies in the country today.
          </p>
        </div>

        {/* Story Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="space-y-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
              Engaging, Customer-Focused, and Technologically Savvy
            </h2>
            <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
              With an unparalleled potential of growth, both commercially and
              residentially, our team specializes in finding the right fit for
              our clients, properties, and communities. We are committed to a
              culture of diversity and corporate responsibility to serve our
              clients with integrity and transparency.
            </p>
            <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
              Whether you are an institutional investor with a multi-unit
              commercial complex or a single-family homeowner seeking reliable
              passive income, Feingold delivers tailored management solutions
              designed for stability and cash flow optimization.
            </p>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200">
              <div className="p-4 rounded-xl bg-white dark:bg-slate-900 shadow-sm border border-slate-200/80">
                <div className="font-extrabold text-2xl text-primary">100%</div>
                <div className="text-xs text-slate-500 font-medium">
                  Transparency Guarantee
                </div>
              </div>
              <div className="p-4 rounded-xl bg-white dark:bg-slate-900 shadow-sm border border-slate-200/80">
                <div className="font-extrabold text-2xl text-accent">24/7</div>
                <div className="text-xs text-slate-500 font-medium">
                  Digital Portal Support
                </div>
              </div>
            </div>
          </div>

          <div>
            <img
              src="https://vibe.filesafe.space/1785535147473014979/assets/71af8882-c85f-4bb2-8478-08015eb3faf6.jpg"
              srcSet="https://vibe.filesafe.space/1785535147473014979/assets/71af8882-c85f-4bb2-8478-08015eb3faf6.jpg 640w, https://vibe.filesafe.space/1785535147473014979/assets/71af8882-c85f-4bb2-8478-08015eb3faf6.jpg 1280w"
              sizes="(max-width: 768px) 100vw, 50vw"
              width={1280}
              height={480}
              alt="Feingold Property Management Team and Portfolio"
              className="rounded-2xl shadow-xl border border-slate-200 object-cover w-full h-[400px]"
              loading="lazy"
            />
          </div>
        </div>

        {/* Core Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200/80 shadow-md">
            <div className="p-3 rounded-xl bg-primary/10 text-primary w-fit mb-4">
              <Shield className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              Integrity & Transparency
            </h3>
            <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">
              We operate with clear, upfront pricing and zero hidden surcharges.
              All owner reports are accessible anytime online.
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200/80 shadow-md">
            <div className="p-3 rounded-xl bg-accent/20 text-accent w-fit mb-4">
              <Users className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              Diversity & Inclusion
            </h3>
            <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">
              We cultivate a culture of corporate responsibility and equal
              housing opportunity for all communities we touch.
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200/80 shadow-md">
            <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-600 w-fit mb-4">
              <Heart className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              Community Dedicated
            </h3>
            <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">
              We cultivate strong relationships and foster thriving residential
              and commercial environments across all communities we manage.
            </p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-3xl mx-auto">
          <LeadConsultationForm title="Connect With Our Leadership Team" />
        </div>
      </div>
    </div>
  );
};
